import React from 'react';
import { Phone, Wrench, Shield, CheckCircle, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import VillesVoisines from '../components/VillesVoisines';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

import SeoNav from '../components/seo/SeoNav';
import CheckList from '../components/seo/CheckList';
import FAQSection from '../components/seo/FAQSection';
import AvisSection from '../components/seo/AvisSection';
import { CtaSection, SeoFooter } from '../components/seo/CtaFooter';

const FAQ = [
  { q: "Combien de temps pour réparer un Samsung à Saint-Chamond ?", r: "La majorité de nos réparations Samsung (écran, batterie, connecteur USB-C) sont effectuées en 30 à 60 minutes. Les réparations plus complexes comme les dégâts des eaux ou la vitre arrière peuvent prendre quelques heures. Nous vous informons du délai précis après diagnostic." },
  { q: "Quels modèles Samsung réparez-vous ?", r: "Nous réparons tous les modèles Samsung Galaxy : S24, S23, S22, S21, S20, S10, Note 20, Note 10, Z Fold, Z Flip, A54, A34, A53, A52, A51, A50, A14, A13 et tous les autres modèles de la gamme Galaxy." },
  { q: "Utilisez-vous des écrans AMOLED d'origine Samsung ?", r: "Nous utilisons des écrans AMOLED premium de très haute qualité, identiques aux écrans d'origine en termes de luminosité, couleurs et réactivité tactile. Pour les clients qui le souhaitent, nous proposons également des écrans d'origine Samsung (délai et prix supérieurs)." },
  { q: "Quelle garantie sur les réparations Samsung ?", r: "Toutes nos réparations Samsung bénéficient d'une garantie de 3 mois pièces et main d'œuvre. Si vous rencontrez le moindre problème avec la réparation effectuée, nous intervenons gratuitement pendant toute la période de garantie." },
  { q: "Quel est le prix d'un écran Samsung ?", r: "Le tarif varie selon le modèle de Galaxy (série A, série S, Note, Z…) et le type d'écran. Demandez votre devis gratuit et immédiat par téléphone ou WhatsApp : nous vous répondons en quelques minutes." },
  { q: "Puis-je avoir un devis avant réparation ?", r: "Absolument ! Nous établissons toujours un devis gratuit et détaillé avant toute intervention. Le devis précise la panne, la pièce à remplacer, le prix total et le délai de réparation. Nous ne procédons qu'après votre accord." },
  { q: "Mes données seront-elles conservées ?", r: "Oui, toutes vos données (photos, contacts, applications) restent intactes lors d'une réparation matérielle. Nous ne touchons jamais au contenu de votre Samsung. Cependant, nous vous recommandons de faire une sauvegarde Samsung Cloud ou Google avant toute réparation." },
  { q: "Réparez-vous la vitre arrière des Samsung ?", r: "Oui, nous remplaçons la vitre arrière (back glass) des Samsung Galaxy. Cette réparation est plus complexe que l'écran avant et prend généralement 1 à 2 heures. Le tarif varie selon le modèle : devis gratuit sur simple demande." },
];

const AVIS_PAGE = [
  { text: "Mon Samsung S22 avait l'écran complètement noir. Suro Phone a changé l'écran AMOLED en 1h et maintenant il fonctionne parfaitement. Excellent travail !", name: "Laurent B.", ville: "Saint-Chamond" },
  { text: "Batterie de mon Galaxy A52 changée rapidement. Le téléphone tient maintenant toute la journée. Service professionnel et prix correct. Je recommande !", name: "Céline F.", ville: "Saint-Chamond" },
  { text: "Mon S21 ne chargeait plus. Le connecteur USB-C a été réparé le jour même avec garantie. Très satisfait du service et de l'accueil !", name: "Ahmed K.", ville: "Saint-Chamond" },
];

const ReparationSamsungSaintChamond = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoNav />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a2332] via-[#1e2a3d] to-[#1a2332] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Réparation Samsung Saint-Chamond
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Spécialiste Samsung Galaxy à Saint-Chamond – Réparation rapide, pièces d'origine et garantie 3 mois
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href="tel:0773601521" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <Phone className="w-5 h-5 mr-2" />
                Appeler maintenant
              </Button>
            </a>
            <Link to="/rendez-vous" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                Prendre rendez-vous
              </Button>
            </Link>
            <a href="https://wa.me/33773601521" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">30-60 min</div>
              <div className="text-sm text-gray-300">Réparation rapide</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">3 mois</div>
              <div className="text-sm text-gray-300">Garantie</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Wrench className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">Premium</div>
              <div className="text-sm text-gray-300">Pièces d'origine</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Star className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">+1000</div>
              <div className="text-sm text-gray-300">Clients satisfaits</div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Réparation Samsung Galaxy à Saint-Chamond – Votre expert local</h2>
            
            <p className="text-gray-700 mb-4">
              Votre <strong>Samsung Galaxy</strong> a un problème ? Suro Phone est le spécialiste de la <strong>réparation Samsung à Saint-Chamond</strong>. Que vous possédiez un Samsung Galaxy S24, S23, S22, S21, S20, Note 20, A54, A34, A14 ou tout autre modèle de la gamme Galaxy, nous intervenons rapidement pour tous types de pannes. Notre atelier dispose des équipements professionnels et de l'expertise nécessaire pour redonner vie à votre smartphone Samsung.
            </p>

            <p className="text-gray-700 mb-4">
              Avec plus de 1000 réparations Samsung effectuées, nous maîtrisons parfaitement toutes les spécificités techniques de la marque coréenne. Nos techniciens sont formés aux dernières technologies Samsung (écrans AMOLED, systèmes One UI, capteurs d'empreinte sous écran) et utilisent exclusivement des <strong>pièces détachées premium ou d'origine</strong> pour garantir la qualité de chaque intervention. La plupart de nos <strong>réparations Samsung sont effectuées en 30 à 60 minutes</strong> avec une garantie de 3 mois.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Toutes les réparations Samsung Galaxy à Saint-Chamond</h3>
            
            <p className="text-gray-700 mb-4">
              Nous traitons quotidiennement tous les problèmes rencontrés sur les smartphones Samsung. Les interventions les plus courantes incluent le <strong>remplacement d'écran Samsung</strong> (AMOLED, Super AMOLED ou écran d'origine), le <strong>changement de batterie Samsung</strong> pour résoudre les problèmes d'autonomie, la réparation du connecteur de charge USB-C, et le remplacement de la vitre arrière souvent fissurée sur les Galaxy récents.
            </p>

            <p className="text-gray-700 mb-4">
              Nous intervenons également sur des pannes plus complexes : problèmes de caméra photo (floue, rayée, ne s'ouvre pas), dysfonctionnements du capteur d'empreinte digitale, bugs logiciels et réinitialisations, pannes de haut-parleur ou micro, boutons power ou volume défaillants, et même les dégâts des eaux avec nettoyage ultrasonique. Chaque <strong>réparation Samsung à Saint-Chamond</strong> bénéficie d'une garantie constructeur de 3 mois pièces et main d'œuvre.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Les modèles Samsung que nous réparons</h3>
            
            <p className="text-gray-700 mb-4">
              Notre atelier est équipé pour réparer tous les modèles de smartphones Samsung, des plus récents aux plus anciens. Nous intervenons sur la gamme premium <strong>Samsung Galaxy S</strong> (S24 Ultra, S23, S22, S21, S20, S10), la série <strong>Galaxy Note</strong> (Note 20, Note 10, Note 9), les modèles pliables <strong>Galaxy Z</strong> (Z Fold, Z Flip), et toute la gamme <strong>Galaxy A</strong> très populaire (A54, A34, A14, A13, A53, A52, A51, A50, A40, A20).
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Pourquoi choisir Suro Phone pour votre Samsung ?</h3>
            
            <CheckList items={[
              { titre: "Expertise Samsung :", texte: "Techniciens spécialisés dans les technologies Samsung (AMOLED, One UI, Knox, capteurs sous écran)." },
              { titre: "Réparation rapide :", texte: "95% de nos réparations Samsung effectuées le jour même en 30-60 minutes selon la panne." },
              { titre: "Pièces premium :", texte: "Écrans AMOLED haute qualité, batteries longue durée, composants testés avant et après installation." },
              { titre: "Garantie 3 mois :", texte: "Toutes nos réparations Samsung sont garanties 3 mois pièces et main d'œuvre pour votre tranquillité." },
              { titre: "Prix transparents :", texte: "Devis gratuit et immédiat, sans surprise ni frais cachés." },
              { titre: "Service flexible :", texte: "Atelier à Saint-Chamond ou intervention à domicile selon votre convenance." },
            ]} />

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Le processus de réparation Samsung chez Suro Phone</h3>
            
            <p className="text-gray-700 mb-4">
              Réparer votre Samsung chez Suro Phone est simple et rapide. Contactez-nous par téléphone au <strong>07.73.60.15.21</strong>, via WhatsApp ou en prenant rendez-vous en ligne. Nous effectuons un diagnostic complet gratuit de votre Samsung Galaxy et vous proposons un devis transparent et détaillé. Une fois le devis accepté, nous commandons les pièces si nécessaire (stock important sur place) et procédons à la réparation, généralement le jour même.
            </p>

            <p className="text-gray-700 mb-4">
              Pendant l'intervention, nos techniciens manipulent votre Samsung avec soin dans notre atelier équipé. Après la réparation, nous effectuons une batterie complète de tests : écran tactile, Face ID ou capteur d'empreinte, caméras, connectivité, son, batterie. Vous récupérez votre Samsung Galaxy comme neuf avec un certificat de garantie de 3 mois. Nous vous conseillons également sur l'utilisation et l'entretien de votre appareil pour maximiser sa durée de vie.
            </p>

            <p className="text-gray-700 mb-4">
              Ne laissez pas un Samsung cassé ou en panne vous ralentir. Pour une <strong>réparation Samsung professionnelle et garantie à Saint-Chamond</strong>, faites confiance à Suro Phone. Nous sommes disponibles du lundi au samedi pour vous servir. Intervention possible dans tout Saint-Chamond et les communes environnantes. Votre Samsung Galaxy mérite le meilleur service de réparation !
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Nos services Samsung Galaxy</h2>
            <p className="text-lg text-gray-600">Réparation professionnelle de tous les modèles Samsung</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="text-blue-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Écran Samsung AMOLED</h3>
                <p className="text-gray-600 text-center mb-4">
                  Remplacement d'écran AMOLED pour Samsung Galaxy. Qualité premium identique à l'origine.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Écran AMOLED premium</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Réparation 30-60 min</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Garantie 3 mois</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="text-green-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Batterie Samsung</h3>
                <p className="text-gray-600 text-center mb-4">
                  Changement de batterie Samsung pour retrouver 100% d'autonomie. Batteries haute capacité.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Batterie longue durée</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Intervention rapide</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Garantie 3 mois</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="text-purple-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Autres réparations</h3>
                <p className="text-gray-600 text-center mb-4">
                  Connecteur USB-C, vitre arrière, caméra, haut-parleur, micro, capteur empreinte et plus.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Tous modèles Galaxy</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Diagnostic gratuit</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Devis transparent</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/rendez-vous">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg rounded-lg transition-colors">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <AvisSection titre="Avis de nos clients" avis={AVIS_PAGE} sousTitre="Témoignages de clients Samsung satisfaits" />

      <FAQSection titre="Questions fréquentes" items={FAQ} sousTitre="Réparation Samsung à Saint-Chamond" />

      <CtaSection titre="Votre Samsung a besoin d'être réparé ?" texte="Contactez-nous pour un devis gratuit et une réparation rapide à Saint-Chamond" />

      <VillesVoisines slug="saint-chamond" />

      <SeoFooter texte="© 2026 Suro Phone - Réparation Samsung Saint-Chamond - Tous droits réservés" />
    </div>
  );
};

export default ReparationSamsungSaintChamond;